/**
 * Utilities for resolving base file paths from Site.files.
 * Supports both new format (BasefileDef[]) and legacy object format during migration.
 */
import { BasefileDef, BasefileFunction, Site, SiteFilesLegacy } from "../models/site.model";

const LEGACY_KEY_TO_FUNCTION: Record<string, BasefileFunction> = {
    onlinePageWrapper: BasefileFunction.ONLINE_PAGE_WRAPPER,
    onlineStartPage: BasefileFunction.ONLINE_START_PAGE,
    defaultFile: BasefileFunction.DEFAULT_FILE,
    missingPage: BasefileFunction.MISSING_PAGE,
    forbiddenPage: BasefileFunction.FORBIDDEN_PAGE,
    unauthorizedPage: BasefileFunction.UNAUTHORIZED_PAGE,
    guestNotAllowedPage: BasefileFunction.GUEST_NOT_ALLOWED_PAGE,
};

function isLegacyFiles(files: Site["files"]): files is SiteFilesLegacy {
    return files != null && !Array.isArray(files);
}

/**
 * Returns all base file paths from site.files (new array or legacy object).
 * Used for search exclusion and download exclusion.
 */
export function getBasefilePaths(site: Site | null | undefined): string[] {
    const files = site?.files;
    if (!files) return [];
    if (isLegacyFiles(files)) {
        const paths: string[] = [];
        for (const key of Object.keys(LEGACY_KEY_TO_FUNCTION) as (keyof SiteFilesLegacy)[]) {
            const entry = files[key];
            if (entry && typeof entry === "object" && "path" in entry && typeof (entry as { path: string }).path === "string") {
                const path = (entry as { path: string }).path;
                if (path) paths.push(path);
            }
        }
        const notForDownload = files.notForDownload;
        if (Array.isArray(notForDownload)) {
            paths.push(...notForDownload.filter((p): p is string => typeof p === "string" && p.length > 0));
        }
        return paths;
    }
    return (files as BasefileDef[]).map((d) => d.path).filter((p) => p.length > 0);
}

/**
 * Returns the single path for a given base file function, or null if not set.
 */
export function getBasefilePathByFunction(site: Site | null | undefined, fn: BasefileFunction): string | null {
    const files = site?.files;
    if (!files) return null;
    if (isLegacyFiles(files)) {
        const key = (Object.keys(LEGACY_KEY_TO_FUNCTION) as (keyof SiteFilesLegacy)[]).find(
            (k) => LEGACY_KEY_TO_FUNCTION[k] === fn
        );
        if (!key) return null;
        const entry = files[key];
        if (entry && typeof entry === "object" && "path" in entry) return (entry as { path: string }).path || null;
        return null;
    }
    const def = (files as BasefileDef[]).find((d) => d.function === fn);
    return def?.path ?? null;
}

/**
 * Returns true if the path is one of the site's base file paths.
 */
export function isBasefilePath(site: Site | null | undefined, path: string): boolean {
    return getBasefilePaths(site).includes(path);
}

/**
 * Returns Site.files as BasefileDef[] for editing in admin UI.
 * Converts legacy object format to array so the UI always works with the new shape.
 */
export function getBasefileDefsArray(site: Site | null | undefined): BasefileDef[] {
    const files = site?.files;
    if (!files) return [];
    if (isLegacyFiles(files)) {
        const result: BasefileDef[] = [];
        for (const key of Object.keys(LEGACY_KEY_TO_FUNCTION) as (keyof SiteFilesLegacy)[]) {
            if (key === "notForDownload") continue;
            const entry = files[key];
            if (entry && typeof entry === "object" && "path" in entry) {
                const path = (entry as { path: string }).path;
                if (path?.trim()) {
                    const fn = LEGACY_KEY_TO_FUNCTION[key];
                    if (fn) result.push({ path: path.trim(), function: fn });
                }
            }
        }
        const notForDownload = files.notForDownload;
        if (Array.isArray(notForDownload)) {
            for (const p of notForDownload) {
                if (typeof p === "string" && p.trim()) result.push({ path: p.trim(), function: BasefileFunction.GENERIC });
            }
        }
        return result;
    }
    return [...(files as BasefileDef[])];
}

export function normalizeLayoutName(name: string | null | undefined): string {
    return (name || "").trim().toLowerCase();
}

export function isValidLayoutName(name: string): boolean {
    return /^[A-Za-z0-9_-]+$/.test(name);
}

export function getLayoutBasefileDefs(site: Site | null | undefined): BasefileDef[] {
    return getBasefileDefsArray(site).filter((d) => d.function === BasefileFunction.LAYOUT);
}

export function getLayoutBasefilePath(
    site: Site | null | undefined,
    layoutName: string | null | undefined,
): string | null {
    const normalized = normalizeLayoutName(layoutName);
    const layoutDefs = getLayoutBasefileDefs(site);
    const match = layoutDefs.find((def) => normalizeLayoutName(def.name) === normalized);
    return match?.path ?? null;
}

/** True when the theme has saved layout settings, including configured-empty (`[]`). */
export function isThemeLayoutsConfigured(layouts: BasefileDef[] | undefined): layouts is BasefileDef[] {
    return layouts !== undefined;
}

function comparableLayoutEntries(defs: BasefileDef[] | undefined): Array<{ name: string; path: string }> {
    return (defs || [])
        .filter((def) => def.function === BasefileFunction.LAYOUT)
        .map((def) => ({
            name: normalizeLayoutName(def.name),
            path: (def.path || "").trim(),
        }))
        .filter((def) => def.path.length > 0)
        .sort((a, b) => a.name.localeCompare(b.name) || a.path.localeCompare(b.path));
}

/**
 * Compare layout mappings after Custom paths normalization: layout entries only,
 * trimmed paths, case-insensitive names, order-independent.
 */
export function layoutMappingsEqual(a: BasefileDef[] | undefined, b: BasefileDef[] | undefined): boolean {
    const left = comparableLayoutEntries(a);
    const right = comparableLayoutEntries(b);
    if (left.length !== right.length) {
        return false;
    }
    return left.every((entry, index) => entry.name === right[index].name && entry.path === right[index].path);
}

/**
 * Replace only `function: Layout` entries on site files with the theme copy.
 * Non-layout entries keep their relative order. `themeLayouts` may be `[]`.
 */
export function replaceSiteLayoutMappings(
    siteFiles: Site["files"] | undefined,
    themeLayouts: BasefileDef[],
): BasefileDef[] {
    const current = getBasefileDefsArray({ files: siteFiles } as Site);
    const rest = current.filter((def) => def.function !== BasefileFunction.LAYOUT);
    const nextLayouts = themeLayouts
        .filter((def) => def.function === BasefileFunction.LAYOUT)
        .map((def) => ({
            path: def.path,
            function: def.function,
            ...(def.name !== undefined ? { name: def.name } : {}),
        }));
    return [...rest, ...nextLayouts];
}
